import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { TaskStatus } from '../types/todoTypes';

/**
 * Get Material-UI color for task status
 * Pending: Grey (default)
 * In Progress: Primary Blue (primary)
 * Blocked: Warning Orange (warning)
 * Complete: Success Green (success)
 */
export const getStatusColor = (
  status: TaskStatus
): 'default' | 'primary' | 'warning' | 'success' => {
  switch (status) {
    case 'Pending':
      return 'default';
    case 'In Progress':
      return 'primary';
    case 'Blocked':
      return 'warning';
    case 'Complete':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Format Firestore Timestamp to readable date string
 */
export const formatTimestamp = (timestamp: Timestamp | null): string => {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = timestamp.toDate();
    return format(date, 'MMM dd, yyyy HH:mm');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

/**
 * Format Firestore Timestamp to short date (no time)
 */
export const formatDate = (timestamp: Timestamp | null): string => {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = timestamp.toDate();
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format Firestore Timestamp to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (timestamp: Timestamp | null): string => {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(timestamp);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
};

/**
 * Validate task title
 */
export const validateTitle = (title: string): { valid: boolean; error?: string } => {
  const trimmed = title.trim();

  if (trimmed.length === 0) {
    return { valid: false, error: 'Title is required' };
  }

  if (trimmed.length < 3) {
    return { valid: false, error: 'Title must be at least 3 characters' };
  }

  if (trimmed.length > 100) {
    return { valid: false, error: 'Title must not exceed 100 characters' };
  }

  return { valid: true };
};

/**
 * Validate task description
 */
export const validateDescription = (description: string): { valid: boolean; error?: string } => {
  const trimmed = description.trim();

  if (trimmed.length === 0) {
    return { valid: false, error: 'Description is required' };
  }

  if (trimmed.length < 10) {
    return { valid: false, error: 'Description must be at least 10 characters' };
  }

  if (trimmed.length > 1000) {
    return { valid: false, error: 'Description must not exceed 1000 characters' };
  }

  return { valid: true };
};
